import { HttpClient, HttpEventType } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Mutex } from "async-mutex";
import moment from "moment";
import { IMqttMessage, MqttService } from "ngx-mqtt";
import {
  BehaviorSubject,
  Observable,
  of,
  Subscription,
  throwError,
} from "rxjs";
import {
  debounceTime,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { DataStorage } from "src/model/class/general classes/data-storage";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { GenericID, GenericValueObject } from "src/model/class/general classes/generic-ids";
import { ReportHTML } from "src/model/class/general classes/report-html";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMedia } from "src/model/interface/iMedia";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { AuthenticationService } from "./authentication.service";
import { EventService } from "./event.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})
/**
 *
 *  Report html service manage data about Report html
 *
 *
 */
export class ReporthtmlService
  extends DataStorage
  implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "Reporthtml.service";
  private _reportHTMLStore: GenericApiData = null;
  private _reportHTMLSubject: BehaviorSubject<Array<ReportHTML>> =
    new BehaviorSubject<Array<ReportHTML>>(null);

  _eventSubscriptions: Array<Subscription> = null;

  /**Flag property; if the service ha foud an error in the last API call needed for get the report-html-list from the server, it is set to TRUE;
   * otherwise, it is set to FALSE.
   */
  private _isErrorInGetReportHTMLListAPIRequest: boolean = false;
  private _isErrorNotSupported: boolean = false;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;
  private mutex = new Mutex();

  public needToInitialize: boolean = true;

  public readonly reportHTMLlist = this._reportHTMLSubject.asObservable();

  get isErrorInGetReportHTMLListAPIRequest(): boolean {
    return this._isErrorInGetReportHTMLListAPIRequest;
  }

  get isErrorNotSupported(): boolean {
    return this._isErrorNotSupported;
  }

  constructor(
    private _storageService: LocalStorageService,
    private _http: HttpClient,
    private _mqttService: MqttClientService,
    private _eventService: EventService,
    private _authService: AuthenticationService,
    private _loggerService: LoggerService,
    private _mqttServiceNative: MqttService,
    private _store: Store
  ) {
    super();

    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    /** Subscrive event from Event Service */
    this.subscribeEvents();
    this.subscribeMQTTAddresses();
  }

  /** Destroy event service */
  ngOnDestroy() {
    this._reportHTMLSubject.next(null);
    this._reportHTMLSubject.complete();

    this.unsubscribeEvents();

    this.unsubscribeToMQTTAddresses();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0])
      ).subscribe(() => {
        this.initializeReportHTMLList();
      })
    );

    /** Operation performed on logout */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._storageService.remove(AppSettings.KEY_REPORTHTML);
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "LOCAL MAP-SUBSCRIPTIONS 1: "
        );
        for (const entry of Array.from(
          this._mqttAddressSubscriptionsMap.entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "LOCAL MAP-SUBSCRIPTIONS 1 - ENTRY: " + entry[0]
          );
        }


        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "NARTIVE ARRAY 1."
        );
        for (const entry of Array.from(
          new Map(Object.entries(this._mqttServiceNative.observables)).entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "NARTIVE ARRAY 1 Entry:  " + entry[0]
          );
        }

        await this.unsubscribeToMQTTAddresses();

        this._reportHTMLStore = null;
        this._reportHTMLSubject.next(null);
        this.needToInitialize = true;


        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "LOCAL MAP-SUBSCRIPTIONS 2: "
        );
        for (const entry of Array.from(
          this._mqttAddressSubscriptionsMap.entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "LOCAL MAP-SUBSCRIPTIONS 2 - ENTRY: " + entry[0]
          );
        }


        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "NARTIVE ARRAY 2."
        );
        for (const entry of Array.from(
          new Map(Object.entries(this._mqttServiceNative.observables)).entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "NARTIVE ARRAY 2 Entry:  " + entry[0]
          );
        }
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   *  Initialize public observable list trasmission: it should be call in the costructor of all component/page that you need.
   */
  public Init() {
    if (this.needToInitialize) {
      this.initializeReportHTMLList();

      this.needToInitialize = false;
    }
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.needToInitialize = true;

    this.Init();
  }

  /**
   * Initialize report html list: first it read from storage (via getFromStorage() function)
   * then if storage read failed it tries to retrive data from API
   */
  private initializeReportHTMLList() {
    this.getFromStorage().then((resultReadStorage: GenericApiData) => {
      if (!resultReadStorage) {
        this.GetReportHTML();
      } else {
        this.GetReportHTMLByLastModify(resultReadStorage);
      }
    });
  }

  //#region Mqtt-addresses handling

  async subscribeMQTTAddresses() {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_DB_REPORT_HTML_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_REPORT_HTML_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._reportHTMLStore !== null),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        map(async (message: Array<string>) => {
          /** map operator allow to make parallel execution without cancelling event: when this fired multiple times
           * it perform execeution without waiting previous execution complete
           */
          if (message.length === 3) {
            const release = await this.mutex.acquire();

            try {
              const operationKind = parseInt(message[0]);
              const idReportHTML = parseInt(message[1]);
              const clientID = message[2];

              /** Finding operation kind: Insert, Update, Delete */
              if (operationKind === GenericOperationKind.INSERT) {
                const indexUpdateReportHTML =
                  this._reportHTMLStore.data.findIndex(
                    (reportHTML: ReportHTML) => reportHTML.id === idReportHTML
                  );
                if (indexUpdateReportHTML < 0) {
                  /** Retrive insert report html from api */
                  const insertReportHTML: ReportHTML =
                    await this.GetReportHTMLByID(idReportHTML).catch(
                      (error) => {
                        return null;
                      }
                    );

                  /** Catching error in previous API call */
                  if (insertReportHTML) {
                    this._reportHTMLStore.data.push(insertReportHTML);
                  } else {
                    throw new Error(
                      "OnInsertReportHTML - Error retriving report with id= " +
                      idReportHTML
                    );
                  }
                }
              } else if (operationKind === GenericOperationKind.UPDATE) {
                const indexUpdateReport = this._reportHTMLStore.data.findIndex(
                  (report: ReportHTML) => report.id === idReportHTML
                );

                /** Retrive update report html from api and subribe it to mqtt logic */
                if (indexUpdateReport >= 0) {
                  const updateReportHTML: ReporthtmlService =
                    await this.GetReportHTMLByID(idReportHTML).catch(
                      (error) => {
                        return null;
                      }
                    );

                  /** Retrive update report html from api */
                  if (updateReportHTML) {
                    this._reportHTMLStore.data[indexUpdateReport] =
                      updateReportHTML;
                  } else {
                    throw new Error(
                      "OnUpdateReportHTML - Error retriving report HTML with id= " +
                      idReportHTML
                    );
                  }
                } else {
                  throw new Error(
                    "OnUpdateReportHTML report HTML not found id= " +
                    idReportHTML
                  );
                }
              } else if (operationKind === GenericOperationKind.DELETE) {
                const currentReportHTML: ReportHTML =
                  this._reportHTMLStore.data.find(
                    (report: ReportHTML) => report.id === idReportHTML
                  );

                this._reportHTMLStore.data = this._reportHTMLStore.data.filter(
                  (report: ReportHTML) => report.id !== idReportHTML
                );
              }
              return idReportHTML + "|" + clientID;
            } finally {
              release();
            }
          } else {
            throw new Error("OnReportDataChange - Incorrect format message");
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDReportHTML) => {
          promiseIDReportHTML.then((IDReportHTMLChanged_ClientID) => {
            if (IDReportHTMLChanged_ClientID) {
              const values = IDReportHTMLChanged_ClientID.split("|");

              /** Update store with report html list changed and propagate modify */
              this._reportHTMLStore.lastAPIModified = moment().format(
                MomentFormats.MOMENT_DATETIME_FORMAT
              );
              this._reportHTMLSubject.next(this._reportHTMLStore.data);

              this._eventService.OnReportHTMLChange.next(
                IDReportHTMLChanged_ClientID
              );

              this.setToStorage(this._reportHTMLStore);
            }
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress",
            "AppReserved.RESERVED_DB_REPORT_HTML_CHANGED",
            "Error in message format: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  //#region Report HTML API Operations
  /**
   *
   * @param idReport id ReportHTML to delete
   *
   * Delete report HTML item by id.
   * It cast Observable to Promise (with .toPromsie())
   * and then update private list, storage list and public observable list, otherwise return error.
   * As update and insert operation, store updates are made by mqtt event subscription
   */
  async DeleteReportHTML(idReportHTML: number): Promise<any> {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_REPORT_HTML +
        "/" +
        idReportHTML
      )
      .pipe(
        tap((cancel) => {
          const currentReportHTML: ReportHTML = this._reportHTMLStore.data.find(
            (reportHTML: ReportHTML) => reportHTML.id === idReportHTML
          );

          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_REPORT_HTML_CHANGED;

          const mqttMessage =
            GenericOperationKind.DELETE.toString() +
            "|" +
            currentReportHTML.id.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);
        })
      )
      .toPromise();
  }

  /**
   *
   * @param reportHTMLData: data report HTML to update
   *
   * Update report HTML item by id.
   * As insert and delete operation, store updates are made by mqtt event subscription
   */
  async UpdateReportHTML(
    idReportHTML: number,
    reportHTMLData: ReportHTML
  ): Promise<ReportHTML> {
    const indexReportHTML = this._reportHTMLStore.data.findIndex(
      (report) => idReportHTML === report.id
    );

    if (indexReportHTML >= 0) {
      if (!reportHTMLData.html.startsWith("<HTML>"))
        reportHTMLData.html =
          "<HTML><BODY>" + reportHTMLData.html + "</BODY></HTML>";

      return this._http
        .put(
          environment.httpServerRoot +
          AppSettings.API_URL_REPORT_HTML +
          "/" +
          idReportHTML,
          reportHTMLData
        )
        .pipe(
          tap((updateReportHTML: ReportHTML) => {
            const mqttAddress =
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
              "_" +
              AppReserved.RESERVED_DB_REPORT_HTML_CHANGED;

            const mqttMessage =
              GenericOperationKind.UPDATE.toString() +
              "|" +
              updateReportHTML.id.toString();

            /** Fire propagation to all connected client */
            this.publishAddress(mqttAddress, mqttMessage);
          })
        )
        .toPromise()
        .then(
          (updateReportHTML: ReportHTML) => {
            return updateReportHTML;
          },
          (error) => {
            throw new Error(error);
          }
        );
    } else {
      return Promise.reject();
    }
  }

  /**
   * Export the report html as a json file
   * @param idReportHTML id of the report to export
   * @returns
   */
  async ExportReportHTML(idReportHTML: number): Promise<GenericValueObject> {
    let request: GenericID = {
      ID: idReportHTML,
    };

    return this._http
      .post<GenericValueObject>(
        environment.httpServerRoot + AppSettings.API_URL_REPORT_HTML_EXPORT,
        request
      )
      .toPromise()
      .then(
        (result: GenericValueObject) => {
          return result;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   * Upload single report HTML to server
   *
   * @param fileToUpload single file to upload to server
   */
  public ImportReportHTML(fileToUpload: File): Promise<any> {
    let formData = new FormData();
    formData.append("fileToImport", fileToUpload);

    /** Set it to observe body upload (ex. for calculating percentace of upload) */
    const config = {
      reportProgress: true,
      observe: "events" as "body",
    };

    return this._http
      .post(
        environment.httpServerRoot + AppSettings.API_URL_REPORT_HTML_IMPORT,
        formData,
        config
      )
      .pipe(
        tap((event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            const percenteUploaded: number = Math.round(
              (100 * event.loaded) / event.total
            );
            this._eventService.OnMediaUploadInProgressEvent.next(
              percenteUploaded
            );
          } else if (event.type === HttpEventType.Response) {
            const mediaUploaded: iMedia = event.body;

            this._eventService.OnMediaUploadComplete.next(mediaUploaded);
          } else if (event.type === HttpEventType.Sent) {
            this._eventService.OnMediaUploadInProgressEvent.next(0);
          } else {
            /** If object emit with status different to 200, something went wrong */
            if (event.status !== 200) {
              this._eventService.OnMediaUploadAbort.next(null);
            }
          }
        })
      )
      .toPromise();
  }

  /**
   * @param reportHTMLData: data report HTML to insert
   *
   * Insert new report HTML item.
   * As update and delete operation, store updates are made via mqtt subscription
   */
  async InsertReportHTML(reportHTMLData: ReportHTML): Promise<ReportHTML> {
    if (!reportHTMLData.html.startsWith("<HTML>"))
      reportHTMLData.html =
        "<HTML><BODY>" + reportHTMLData.html + "</BODY></HTML>";

    return this._http
      .post<ReportHTML>(
        environment.httpServerRoot + AppSettings.API_URL_REPORT_HTML + "/",
        reportHTMLData
      )
      .pipe(
        tap((insertReportHTML: ReportHTML) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_REPORT_HTML_CHANGED;

          const mqttMessage =
            GenericOperationKind.INSERT.toString() +
            "|" +
            insertReportHTML.id.toString();

          this.publishAddress(mqttAddress, mqttMessage);
        })
      )
      .toPromise()
      .then(
        (insertReportHTML: ReportHTML) => {
          return insertReportHTML;
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_http.post -  AppSettings.API_URL_REPORT_HTML ",
            "InsertReportHTML()",
            "Insert report html Error: " + error.message
          );
          throw new Error(error);
        }
      );
  }

  /**
   * Get report html data from API. If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetReportHTMLListAPIRequest" to
   * TRUE, it set the store to NULL and emit on the osbservable a neutral value (IE. empty array).
   */
  GetReportHTML(): Subscription {
    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_REPORT_HTML
      )
      .subscribe(
        (returnReportHTMLStore: GenericApiData) => {
          //Subscribing mqtt report html address

          this._isErrorInGetReportHTMLListAPIRequest = false;

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_http.get -  AppSettings.API_URL_REPORT_HTML ",
            "GetReportHTML()",
            "OKAY API"
          );

          this._reportHTMLStore = returnReportHTMLStore;
          this._reportHTMLSubject.next(returnReportHTMLStore.data);

          this.setToStorage(returnReportHTMLStore);
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_http.get -  AppSettings.API_URL_REPORT_HTML ",
            "GetReportHTML()",
            "Error in list retrieval from server: " + error.message
          );

          this.handlePeculiarApiResponseError(error);
          this._isErrorInGetReportHTMLListAPIRequest = true;
          this._reportHTMLStore = null;
          this._reportHTMLSubject.next([]);
        }
      );
  }

  /**
   * Handle errors in api call which require peculiar reactions from the page; this includes cases where the error is propagated through subject.error()
   * @param error
   */
  private handlePeculiarApiResponseError(error: any) {
    switch (error.status) {
      /*This usually is associated with attempts by the app to require access to server-side features which are NOT currently supported*/
      case ApiErrorType.NOT_FOUND:
        this._isErrorNotSupported = true;
        break;
      default:
        break;
    }
  }

  /**
   * Get report html data from API with last modify data store.
   * If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetReportHTMLListAPIRequest" to
   * TRUE, it leaves the store as it is and emit on the osbservable a neutral value (IE. empty array).
   * If original data store isn't changed, api return nothin (code 200)
   */
  private GetReportHTMLByLastModify(reportHTMLStore: GenericApiData) {
    const queryString: string =
      "?lastModified=" + encodeURIComponent(reportHTMLStore.lastAPIModified);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_REPORT_HTML +
        queryString
      )
      .subscribe(
        (returnReportHTMLStore: GenericApiData) => {
          this._isErrorInGetReportHTMLListAPIRequest = false;

          // this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_http.get -  AppSettings.API_URL_REPORT_HTML ", "GetReportHTMLByLastModify()", "END");

          if (returnReportHTMLStore) {
            reportHTMLStore = returnReportHTMLStore;

            this.setToStorage(reportHTMLStore);
          }

          this._reportHTMLStore = reportHTMLStore;

          //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_http.get -  AppSettings.API_URL_REPORT_HTML ", "GetReportHTMLByLastModify()", "reportHTMLStore retrieved from server: "+JSON.stringify(reportHTMLStore.data));

          this._reportHTMLSubject.next(reportHTMLStore.data);
        },
        (error) => {
          this.handlePeculiarApiResponseError(error);
          this._isErrorInGetReportHTMLListAPIRequest = true;
          this._reportHTMLSubject.next([]);
        }
      );
  }

  /**
   * Get report html data from API. If api call fallback in error costructor, it propagate an Observable notification with .error() function
   */
  private async GetReportHTMLByID(idReportHTML: number) {
    const queryString: string = "?id=" + idReportHTML;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_REPORT_HTML +
        queryString
      )
      .pipe(
        switchMap((returnReportHTMLList: GenericApiData) => {
          /** Clean API object from unnecessary data
           *  and check if data key is populated: if length is equal to zero, no
           *  report html is finded
           */
          if (returnReportHTMLList.data.length > 0) {
            const returnReportHTML: ReportHTML = returnReportHTMLList.data[0];

            return of(returnReportHTML);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise()
      .then(
        (returnReportHTML: ReportHTML) => {
          return returnReportHTML;
        },
        (error) => {
          throw new Error();
        }
      );
  }
  //#endregion

  //#region Storage
  protected getFromStorage(): Promise<object> {
    //get from storage and insert in local list of report HTML
    return this._storageService.get(AppSettings.KEY_REPORTHTML).then(
      (reportHTMLStorage: GenericApiData) => {
        if (reportHTMLStorage) {
          return reportHTMLStorage;
        } else {
          return null;
        }
      },
      (error) => {
        return null;
      }
    );
  }

  protected setToStorage(reportHTMLStore: GenericApiData) {
    this._storageService.set(AppSettings.KEY_REPORTHTML, reportHTMLStore);
  }

  //#endregion

  publishAddress(mqttAddress: string, value: string) {
    this._mqttService.publishAddress(mqttAddress, value);
  }
}
